import { useReducer, useEffect, useRef } from 'react';
import { isEmpty, pick, keys, without } from 'ramda';

const exist = (picker, container) => !isEmpty(pick([picker], container));

const reducerState = (reducers) => (state, { type, payload }) => {
  const Exist = exist(type, reducers);
  return Exist ? reducers[type](state, payload) : new Error(`dispatch ${type} not found`);
};

const useModel = ({ state: initialState, reducers = {}, effects = {} }) => {
  const [loading, loadingDispatch] = useReducer((s, action) => ({ ...s, ...action }), {});
  const [state, dispatch] = useReducer(reducerState(reducers), initialState);

  useEffect(() => {
    const effectsKeys = keys(effects);
    const reducesKeys = keys(reducers);
    const filteredKeys = without(reducesKeys, effectsKeys);
    if (effectsKeys.length !== filteredKeys.length) {
      const errorMessage = `
        reducers and effects expect different keys, the same key will leads to a stack-overflow.
        please checkout the same keys below:
        [effect]: ${effectsKeys.join(' ')};
        [reducers]: ${reducesKeys.join(' ')};
      `;
      throw Error(errorMessage);
    }
  }, [effects, reducers]);

  const _dispatch = (type) => async (payload) => {
    const isAsync = !isEmpty(pick([type], effects));
    if (isAsync) {
      loadingDispatch({ [type]: true });
      const result = await effects[type]({
        dispatch: _dispatch, // (type) => (payload) => dispatch({ type, payload }),
        state,
        payload,
      });
      loadingDispatch({ [type]: false });
      return result;
    }
    dispatch({ type, payload });
  };

  const dispatchRef = useRef(_dispatch);
  useEffect(() => {
    dispatchRef.current = _dispatch;
  });

  return {
    state,
    loading,
    dispatch: dispatchRef.current,
  };
};

export default useModel;
